"use client"
import { useState, useEffect } from 'react';
import './index.css';
import { CHART_COLORS } from '@/utils/dummySelectsData';

interface CryptoData {
    name: string;
    amount: number;
}

interface PieDiagramProps {
    data: CryptoData[];
}

// Функция для перевода (x%) процента в [x, y] координаты для SVG <path/>
const polarToCartesian = (percentage: number): [number, number] => {
    const radians = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
    return [16 + 16 * Math.cos(radians), 16 + 16 * Math.sin(radians)];
};

const PieDiagram = ({ data }: PieDiagramProps) => {
    const total = data.reduce((sum, crypto) => sum + crypto.amount, 0);
    const [display, setDisplay] = useState<{ amount: number; currency: string }>({
        amount: total,
        currency: 'Total',
    });

    useEffect(() => {
        setDisplay({ amount: total, currency: 'Total' });
    }, [data, total]);

    let cumulativePercentage = 0;

    const paths = data.length > 1 ? data.map((crypto, index) => {
        const percentage = (crypto.amount / total) * 100;
        const [startX, startY] = polarToCartesian(cumulativePercentage);
        cumulativePercentage += percentage;
        const [endX, endY] = polarToCartesian(cumulativePercentage);
        const largeArcFlag = percentage > 50 ? 1 : 0;

        return (
            <g key={crypto.name}>
                <path
                    d={`M 16 16 L ${startX} ${startY} A 16 16 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                    className={`animate-dash transition-all duration-1000 ease-out hover:fill-slate-800 dark:hover:fill-slate-600`}
                    onMouseEnter={() => setDisplay({ amount: crypto.amount, currency: crypto.name })}
                    onMouseLeave={() => setDisplay({ amount: total, currency: 'Total' })}
                />
            </g>
        );
    }) : data.length === 1 ?
        <circle
            key={data[0].name}
            cy={'50%'}
            cx={'50%'}
            r={16}
            strokeDasharray={'0 100'}
            className={`animate-dash transition-all duration-1000 ease-out hover:fill-slate-800 dark:hover:fill-slate-600`}
            fill={CHART_COLORS[0]}
            onMouseEnter={() => setDisplay({ amount: data[0].amount, currency: data[0].name })}
            onMouseLeave={() => setDisplay({ amount: total, currency: 'Total' })}
        /> :
        <text x="16" y="16" textAnchor="middle" alignmentBaseline="middle">No Data</text>;

    return (
        <div className="relative w-full h-[250px] mt-4">
            <svg width="100%" height="100%" viewBox="0 0 32 32">
                {paths}
                <circle cx="16" cy="16" r="8" className='dark:fill-slate-900 fill-white' />
            </svg>
            <div className="absolute flex flex-col items-center justify-center text-black dark:text-white text-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="text-xs dark:bg-slate-900 bg-white p-1 rounded">{display.currency} amount</div>
                <div className="text-xs font-bold">${display.amount.toLocaleString()}</div>
            </div>
        </div>
    );
};

export default PieDiagram;